// Development Studies Student Notes - Years 11-13
// Comprehensive study materials

export const year11DevelopmentStudentNotes = {
  year: 11,
  subject: "Development Studies",
  strands: {
    strand1: {
      name: "What is Development?",
      units: [
        {
          id: "dst1-1",
          name: "Nature and Aspects of Development",
          objectives: [
            "Define the term 'development'",
            "Identify the different aspects of development",
            "Explain why development is more than economic growth"
          ],
          keyTerms: [
            { term: "Development", definition: "A process of change that improves the quality of life for individuals and societies" },
            { term: "Economic Development", definition: "Growth in the production and distribution of goods and services" },
            { term: "Social Development", definition: "Improvements in education, health, housing, and social services" },
            { term: "Sustainable Development", definition: "Development that meets present needs without compromising future generations" },
            { term: "Quality of Life", definition: "The general well-being of individuals and societies" }
          ],
          studyNotes: `## What is Development?

### Definition
Development is a multi-dimensional process of change that improves the quality of life for individuals and communities. It goes beyond just economic growth.

### The Six Aspects of Development
Development has SIX interconnected aspects:

1. **Economic Development**
   - Growth in production, trade, income
   - Job creation and employment
   - Infrastructure development

2. **Social Development**
   - Better education and healthcare
   - Improved housing and sanitation
   - Social welfare programs

3. **Political Development**
   - Good governance and democracy
   - Human rights protection
   - Citizen participation

4. **Cultural Development**
   - Preservation of traditions
   - Arts and heritage
   - Cultural identity

5. **Environmental Development**
   - Resource conservation
   - Pollution control
   - Sustainable practices

6. **Spiritual Development**
   - Religious freedom
   - Moral and ethical values
   - Community harmony

### The Development Star
These six aspects can be shown as a star diagram with development at the center. All aspects are equally important and interconnected.

### Key Point
Development does NOT always mean economic development. A country can be economically rich but have poor social services, environmental destruction, or human rights abuses.`,
          practiceQuestions: [
            "Define 'development' in your own words",
            "List the six aspects of development",
            "Why is development more than just economic growth?",
            "Draw a development star diagram showing all six aspects",
            "Give an example of a country with high economic growth but low social development"
          ],
          examTips: "Remember all SIX aspects of development. In essays, always explain how different aspects are interconnected."
        },
        {
          id: "dst1-2",
          name: "Development Indicators",
          objectives: [
            "Name indicators used to measure development",
            "Define GDP, GNP, and PPP",
            "Explain the Human Development Index",
            "Discuss limitations of development indicators"
          ],
          keyTerms: [
            { term: "GDP (Gross Domestic Product)", definition: "The total value of goods and services produced within a country in one year" },
            { term: "GNP (Gross National Product)", definition: "GDP plus income earned by citizens abroad, minus income earned by foreigners in the country" },
            { term: "GDP per capita", definition: "GDP divided by population - shows average income per person" },
            { term: "PPP (Purchasing Power Parity)", definition: "Adjustment to account for different price levels between countries" },
            { term: "HDI (Human Development Index)", definition: "A composite measure combining life expectancy, education, and income" },
            { term: "Infant Mortality Rate", definition: "Number of babies who die before age 1, per 1000 live births" }
          ],
          studyNotes: `## Development Indicators

### Why Measure Development?
Indicators help us:
- Compare development levels between countries
- Track progress over time
- Identify areas needing improvement

### Economic Indicators
| Indicator | What it measures | Formula |
|-----------|------------------|---------|
| GDP | Total production | Sum of all goods and services |
| GDP per capita | Average income | GDP ÷ Population |
| GNP | National income | GDP + income from abroad - foreign income |
| PPP | Comparable purchasing power | Adjusts for price differences |

### Social Indicators
| Indicator | What it measures |
|-----------|------------------|
| Life expectancy | Average years a person will live |
| Literacy rate | % of adults who can read and write |
| Infant mortality | Deaths under 1 year per 1000 births |
| Access to clean water | % with safe drinking water |

### Human Development Index (HDI)
HDI combines THREE indicators:
1. **Life expectancy** (health)
2. **Education** (mean years of schooling)
3. **GNI per capita** (living standard)

HDI ranges from 0 to 1:
- Very High: 0.8 and above
- High: 0.7 - 0.799
- Medium: 0.55 - 0.699
- Low: Below 0.55

### Limitations of Indicators
1. **GDP doesn't show distribution** - wealth may be unequal
2. **Statistics can be inaccurate** - especially in developing countries
3. **Some things can't be measured** - happiness, freedom, culture
4. **Averages hide inequalities** - rich and poor averaged together`,
          practiceQuestions: [
            "What does GDP per capita measure?",
            "What three components make up the HDI?",
            "Calculate GDP per capita for a country with GDP of $10 billion and population of 250,000",
            "Why might GDP per capita be misleading?",
            "Name three social development indicators"
          ],
          examTips: "Know the formulas for GDP per capita and be able to discuss limitations of using GDP as a development measure."
        }
      ]
    },
    strand2: {
      name: "Economic Development",
      units: [
        {
          id: "dst2-1",
          name: "Economic Systems",
          objectives: [
            "Define economic development and economic systems",
            "Identify different economic systems in Vanuatu",
            "Compare subsistence and commercial economies"
          ],
          keyTerms: [
            { term: "Subsistence Economy", definition: "An economy where people produce mainly for their own consumption, not for sale" },
            { term: "Commercial Economy", definition: "An economy based on buying and selling goods and services for profit" },
            { term: "Mixed Economy", definition: "An economy with both subsistence and commercial elements" },
            { term: "Traditional Economy", definition: "An economy based on customs, traditions, and barter exchange" }
          ],
          studyNotes: `## Economic Systems

### Types of Economic Systems

**1. Subsistence Economy**
- People produce for their own needs
- Little trade or money exchange
- Common in rural areas of Vanuatu
- Based on fishing, gardening, hunting

**2. Commercial Economy**
- Goods produced for sale
- Uses money for exchange
- Based on markets and trade
- Found in urban areas

**3. Mixed Economy (Vanuatu's System)**
- Combines subsistence and commercial
- Rural areas: mostly subsistence
- Urban areas: mostly commercial
- Both systems coexist

### Sectors of Economic Activity
| Sector | Activities | Vanuatu Examples |
|--------|-----------|------------------|
| Primary | Extracting raw materials | Fishing, farming, logging |
| Secondary | Manufacturing goods | Copra processing, handicrafts |
| Tertiary | Providing services | Tourism, banking, transport |
| Quaternary | Knowledge/technology | IT, research, education |

### Vanuatu's Economy
- Primary sector dominates (agriculture, fishing)
- Growing tertiary sector (tourism)
- Limited secondary sector (manufacturing)
- Traditional economy still important`,
          practiceQuestions: [
            "What is a subsistence economy?",
            "Give three examples of primary sector activities in Vanuatu",
            "Why does Vanuatu have a mixed economy?",
            "Name the four sectors of economic development",
            "Why is tourism important for Vanuatu's economy?"
          ],
          examTips: "Be able to give Vanuatu-specific examples for each economic sector."
        }
      ]
    },
    strand3: {
      name: "Promoting Development",
      units: [
        {
          id: "dst3-1",
          name: "International Aid",
          objectives: [
            "Define international aid and related terms",
            "Identify sources and forms of aid",
            "Discuss advantages and disadvantages of aid"
          ],
          keyTerms: [
            { term: "International Aid", definition: "Assistance given by governments and organizations to help developing countries" },
            { term: "ODA (Official Development Assistance)", definition: "Aid from government sources, given on favorable terms" },
            { term: "Bilateral Aid", definition: "Aid given directly from one country to another" },
            { term: "Multilateral Aid", definition: "Aid given through international organizations like the UN or World Bank" },
            { term: "NGO", definition: "Non-Governmental Organization - private groups that provide aid and services" },
            { term: "Aid Dependency", definition: "When a country relies too heavily on foreign aid" }
          ],
          studyNotes: `## International Aid

### Types of Aid
1. **Bilateral Aid** - Country to country (Australia to Vanuatu)
2. **Multilateral Aid** - Through organizations (UN, World Bank)
3. **NGO Aid** - From private organizations (Red Cross, Oxfam)

### Forms of Aid
- **Financial grants** - Money that doesn't need repayment
- **Loans** - Money that must be repaid (sometimes low interest)
- **Technical assistance** - Experts and training
- **Food aid** - Emergency food supplies
- **Equipment** - Vehicles, medical supplies, etc.

### Major Aid Donors in the Pacific
| Donor | Type of Aid |
|-------|-------------|
| Australia (DFAT) | Bilateral, largest Pacific donor |
| New Zealand (MFAT) | Bilateral |
| Japan (JICA) | Infrastructure, technical |
| European Union | Development programs |
| China | Infrastructure, loans |

### Advantages of Aid
✓ Helps build infrastructure
✓ Provides emergency relief
✓ Supports education and health
✓ Transfers technology and skills

### Disadvantages of Aid
✗ Creates dependency
✗ May come with conditions ("tied aid")
✗ Can support corruption
✗ May not reach those who need it
✗ Can distort local markets`,
          practiceQuestions: [
            "What is the difference between bilateral and multilateral aid?",
            "Name three major aid donors in the Pacific",
            "What is meant by 'tied aid'?",
            "Give two advantages and two disadvantages of foreign aid",
            "Why might aid create dependency?"
          ],
          examTips: "Learn specific examples of aid projects in Vanuatu. Be balanced when discussing advantages and disadvantages."
        }
      ]
    },
    strand4: {
      name: "Development and Environment",
      units: [
        {
          id: "dst4-1",
          name: "The Earth's Natural Environment",
          objectives: [
            "Identify features of the Earth's environment",
            "Describe climate zones and biomes",
            "Explain the greenhouse effect"
          ],
          keyTerms: [
            { term: "Biome", definition: "A large ecosystem characterized by distinctive climate, plants, and animals" },
            { term: "Greenhouse Effect", definition: "The trapping of heat in Earth's atmosphere by greenhouse gases" },
            { term: "Global Warming", definition: "The gradual increase in Earth's average temperature" },
            { term: "Climate Change", definition: "Long-term changes in temperature, rainfall, and weather patterns" },
            { term: "Biodiversity", definition: "The variety of plant and animal species in an environment" }
          ],
          studyNotes: `## Earth's Natural Environment

### Climate Zones
| Zone | Location | Characteristics |
|------|----------|-----------------|
| Tropical | Near equator | Hot, high rainfall |
| Subtropical | 20-35° latitude | Warm, seasonal rain |
| Temperate | 35-60° latitude | Moderate, four seasons |
| Arctic/Polar | Near poles | Very cold, little rain |

### Vanuatu's Climate
- Tropical maritime climate
- Hot and humid year-round
- Wet season (Nov-April) with cyclones
- Dry season (May-October)

### The Greenhouse Effect
1. Sun's energy enters atmosphere
2. Earth absorbs and re-radiates heat
3. Greenhouse gases trap some heat
4. This keeps Earth warm enough for life

**Greenhouse Gases:**
- Carbon dioxide (CO₂) - from burning fossil fuels
- Methane (CH₄) - from agriculture, landfills
- Nitrous oxide (N₂O) - from fertilizers

### Climate Change Impacts on Vanuatu
- Rising sea levels (coastal flooding)
- More intense cyclones
- Coral bleaching
- Changes in rainfall patterns
- Threats to agriculture`,
          practiceQuestions: [
            "What type of climate does Vanuatu have?",
            "Name three greenhouse gases",
            "Explain the greenhouse effect in your own words",
            "How might sea level rise affect Vanuatu?",
            "What is the difference between global warming and climate change?"
          ],
          examTips: "Be able to explain climate change impacts specific to Pacific Island countries like Vanuatu."
        },
        {
          id: "dst4-2",
          name: "Natural Resources",
          objectives: [
            "Define natural resources",
            "Classify resources as renewable and non-renewable",
            "Discuss sustainable resource use"
          ],
          keyTerms: [
            { term: "Natural Resource", definition: "Materials found in nature that are useful to humans" },
            { term: "Renewable Resource", definition: "A resource that can be replaced naturally over time (forests, fish)" },
            { term: "Non-renewable Resource", definition: "A resource that cannot be replaced once used (oil, minerals)" },
            { term: "Sustainable Use", definition: "Using resources at a rate that allows them to be replaced" },
            { term: "Conservation", definition: "Careful management and protection of natural resources" }
          ],
          studyNotes: `## Natural Resources

### Classification of Resources

**Non-renewable (Stock) Resources:**
- Fossil fuels (oil, coal, gas)
- Minerals (gold, copper, iron)
- Once used, gone forever

**Renewable (Flow) Resources:**
- Forests (can regrow)
- Fish stocks (can reproduce)
- Fresh water (water cycle)
- Must be managed sustainably

**Continuous Resources:**
- Solar energy
- Wind energy
- Tidal/wave energy
- Always available

### Vanuatu's Natural Resources
| Resource | Use | Concerns |
|----------|-----|----------|
| Forests | Timber, land clearing | Deforestation |
| Fish | Food, export | Overfishing |
| Land | Agriculture | Erosion |
| Coral reefs | Tourism, fishing | Bleaching, damage |
| Fresh water | Drinking, irrigation | Pollution |

### Sustainable vs Unsustainable Use
**Sustainable:**
- Taking only what can be replaced
- Traditional fishing methods
- Selective logging with replanting
- Protected marine areas (tabu)

**Unsustainable:**
- Overfishing depleting stocks
- Clear-cutting forests
- Polluting water sources
- Destructive mining

### Traditional Conservation
Vanuatu's traditional "tabu" system:
- Areas closed for resource recovery
- Seasonal restrictions
- Community-based management
- Respects natural cycles`,
          practiceQuestions: [
            "What is the difference between renewable and non-renewable resources?",
            "Give two examples of each type of resource",
            "What is meant by 'sustainable use'?",
            "Explain the traditional tabu system",
            "Why is overfishing a problem in the Pacific?"
          ],
          examTips: "Know examples of traditional conservation practices in Vanuatu and why they are effective."
        }
      ]
    },
    strand5: {
      name: "Social, Cultural, Spiritual and Political Development",
      units: [
        {
          id: "dst5-1",
          name: "Human Societies",
          objectives: [
            "Identify features of different societies",
            "Explain how societies have evolved",
            "Compare traditional and modern societies"
          ],
          keyTerms: [
            { term: "Hunter-Gatherer Society", definition: "A society that relies on hunting animals and gathering plants for food" },
            { term: "Agricultural Society", definition: "A society based on farming and raising animals" },
            { term: "Industrial Society", definition: "A society based on manufacturing and factory production" },
            { term: "Post-Industrial Society", definition: "A society based on services and information technology" }
          ],
          studyNotes: `## Development of Human Societies

### Evolution of Societies
1. **Hunter-Gatherer** (100,000+ years ago)
   - Small nomadic groups
   - Gathered plants, hunted animals
   - No permanent settlements

2. **Agricultural** (10,000 years ago)
   - Settled farming communities
   - Domesticated plants and animals
   - Villages and towns developed

3. **Industrial** (250 years ago)
   - Factory-based production
   - Urban migration
   - Technological innovation

4. **Post-Industrial** (50 years ago)
   - Service-based economy
   - Information technology
   - Globalization

### Traditional Vanuatu Society
- Based on subsistence agriculture
- Strong extended family (clan) systems
- Chief-based leadership
- Rich oral traditions and customs
- Communal land ownership

### Changes in Vanuatu Society
- Increasing urbanization
- Cash economy growing
- Western influences
- Challenges to traditional values
- Youth migration to towns`,
          practiceQuestions: [
            "Name the four stages of societal development",
            "What are three features of traditional Vanuatu society?",
            "How has Vanuatu society changed in recent decades?",
            "Compare hunter-gatherer and agricultural societies",
            "What is a post-industrial society?"
          ],
          examTips: "Be able to discuss both the positive and negative aspects of societal change in Vanuatu."
        },
        {
          id: "dst5-2",
          name: "Population Growth",
          objectives: [
            "Locate major population clusters",
            "Describe effects of rapid population growth",
            "Discuss population management strategies"
          ],
          keyTerms: [
            { term: "Population Growth Rate", definition: "The rate at which a population increases or decreases" },
            { term: "Birth Rate", definition: "Number of births per 1000 people per year" },
            { term: "Death Rate", definition: "Number of deaths per 1000 people per year" },
            { term: "Natural Increase", definition: "Birth rate minus death rate" },
            { term: "Population Density", definition: "Number of people per square kilometer" }
          ],
          studyNotes: `## Population Growth

### World Population
- Currently about 8 billion people
- Growing by about 80 million per year
- Most growth in developing countries

### Population Clusters
Major population concentrations:
- East Asia (China, Japan)
- South Asia (India, Bangladesh)
- Europe
- Eastern USA

### Effects of Rapid Population Growth
**Negative Effects:**
- Pressure on resources
- Environmental degradation
- Unemployment
- Overcrowded cities
- Strain on services (schools, hospitals)

**In Vanuatu:**
- Population about 320,000
- Growth rate around 2% per year
- High proportion of young people
- Urban migration to Port Vila

### Managing Population Growth
- Family planning education
- Women's education and empowerment
- Economic development
- Healthcare improvements
- Government policies`,
          practiceQuestions: [
            "What is the current world population?",
            "Name three negative effects of rapid population growth",
            "What is Vanuatu's approximate population?",
            "How is natural increase calculated?",
            "Why is women's education important for population management?"
          ],
          examTips: "Know the effects of population growth specific to Vanuatu and Pacific Island countries."
        }
      ]
    }
  }
};

export const year12DevelopmentStudentNotes = {
  year: 12,
  subject: "Development Studies",
  strands: {
    strand1: {
      name: "What is Development? (Advanced)",
      units: [
        {
          id: "dst1-12",
          name: "Quality of Life Survey",
          objectives: [
            "Design and conduct a quality of life survey",
            "Collect and analyze data",
            "Present findings using appropriate methods"
          ],
          keyTerms: [
            { term: "Quality of Life Survey", definition: "A research method to assess living conditions in a community" },
            { term: "Primary Data", definition: "Data collected directly through surveys, interviews, or observations" },
            { term: "Secondary Data", definition: "Data collected from existing sources like reports and statistics" },
            { term: "Sample", definition: "A subset of a population selected for research" }
          ],
          studyNotes: `## Quality of Life Survey

### Purpose
To assess the quality of life in a local community by collecting data on various development indicators.

### Survey Design Steps
1. **Define objectives** - What do you want to find out?
2. **Select sample** - Who will you survey?
3. **Design questions** - What will you ask?
4. **Collect data** - Conduct interviews
5. **Analyze data** - Look for patterns
6. **Present findings** - Graphs, tables, report

### Types of Questions
- **Closed questions**: Yes/No or multiple choice
- **Open questions**: Allow detailed responses
- **Likert scale**: Rate on a scale (1-5)

### Data Presentation
| Method | Best for |
|--------|----------|
| Bar graph | Comparing categories |
| Pie chart | Showing percentages |
| Line graph | Showing change over time |
| Table | Detailed numerical data |
| Map | Geographic patterns |

### Report Structure
1. Introduction (purpose, area studied)
2. Methods (how data collected)
3. Results (what you found)
4. Analysis (what it means)
5. Conclusion (summary, recommendations)`,
          practiceQuestions: [
            "What is the purpose of a quality of life survey?",
            "List the steps in designing a survey",
            "What is the difference between primary and secondary data?",
            "What type of graph would you use to show percentages?",
            "Name three indicators you might measure in a quality of life survey"
          ],
          examTips: "For the internal assessment, make sure your survey report has all sections and uses appropriate data presentation methods."
        },
        {
          id: "dst1-12b",
          name: "Inequalities in Development",
          objectives: [
            "Define inequality and development gap",
            "Explain patterns of world trade",
            "Analyze effects of colonialism on development"
          ],
          keyTerms: [
            { term: "Development Gap", definition: "The difference in living standards between developed and developing countries" },
            { term: "Colonialism", definition: "The policy of acquiring and exploiting colonies by foreign powers" },
            { term: "MDC/MEDC", definition: "More Developed/Economically Developed Country" },
            { term: "LDC/LEDC", definition: "Least/Less Economically Developed Country" },
            { term: "NIC", definition: "Newly Industrialized Country (e.g., China, India, Brazil)" }
          ],
          studyNotes: `## Inequalities in Development

### The Development Gap
- Huge differences between rich and poor countries
- Gap has widened in recent decades
- Measured by GDP, HDI, life expectancy, etc.

### Classification of Countries
| Term | Description | Examples |
|------|-------------|----------|
| MDC/MEDC | High income, developed | USA, Japan, Germany |
| NIC | Rapidly industrializing | China, India, Brazil |
| LDC/LEDC | Low income, developing | Vanuatu, Haiti, Mali |

### Causes of the Development Gap

**Historical Causes:**
- Colonialism exploited resources
- Unfair trade patterns established
- Unequal power relationships

**Current Causes:**
- Unfair terms of trade
- Debt burden
- Brain drain
- Political instability
- Climate vulnerability

### World Trade Patterns
- Developed countries: export manufactured goods (high value)
- Developing countries: export raw materials (low value)
- "Price fixers" vs "price acceptors"
- Trade rules favor wealthy countries`,
          practiceQuestions: [
            "What is the development gap?",
            "What is the difference between an MDC and an LDC?",
            "How did colonialism contribute to the development gap?",
            "Why do developing countries often get low prices for their exports?",
            "Give three causes of inequality in development"
          ],
          examTips: "Be able to discuss both historical and current causes of the development gap with specific examples."
        }
      ]
    },
    strand2: {
      name: "Economic Development (Advanced)",
      units: [
        {
          id: "dst2-12",
          name: "Food Security and Poverty",
          objectives: [
            "Define food security, poverty, and malnutrition",
            "Analyze causes of food insecurity",
            "Evaluate solutions to food security challenges"
          ],
          keyTerms: [
            { term: "Food Security", definition: "When all people have access to sufficient, safe, nutritious food" },
            { term: "Poverty", definition: "The state of being extremely poor and lacking basic necessities" },
            { term: "Absolute Poverty", definition: "Inability to meet basic needs like food, shelter, clothing" },
            { term: "Malnutrition", definition: "Lack of proper nutrition, whether from insufficient or imbalanced food" },
            { term: "Hunger", definition: "The physical sensation of needing food; chronic lack of food" }
          ],
          studyNotes: `## Food Security and Poverty

### Food Security Dimensions
1. **Availability** - Enough food produced/imported
2. **Access** - People can afford and reach food
3. **Utilization** - Proper nutrition and health
4. **Stability** - Consistent access over time

### Causes of Food Insecurity
- Poverty and low incomes
- Climate change and natural disasters
- Conflict and political instability
- Poor infrastructure
- Population growth
- Land degradation

### Food Insecurity in the Pacific
- Import dependency
- Declining traditional agriculture
- Unhealthy imported foods
- Climate change impacts
- Isolated communities

### Solutions
1. **Improve agriculture**
   - Better farming techniques
   - Drought-resistant crops
   - Sustainable practices

2. **Economic measures**
   - Fair prices for farmers
   - Reduce food waste
   - Improve storage

3. **Social protection**
   - School feeding programs
   - Food aid for emergencies
   - Social safety nets`,
          practiceQuestions: [
            "What are the four dimensions of food security?",
            "Name three causes of food insecurity",
            "What is the difference between hunger and malnutrition?",
            "How does climate change affect food security in the Pacific?",
            "Suggest two solutions to improve food security in Vanuatu"
          ],
          examTips: "Use Pacific-specific examples when discussing food security issues and solutions."
        }
      ]
    }
  }
};

export const year13DevelopmentStudentNotes = {
  year: 13,
  subject: "Development Studies",
  strands: {
    strand1: {
      name: "Theories of Development",
      units: [
        {
          id: "dst1-13",
          name: "Development Theories",
          objectives: [
            "Understand modernization theory",
            "Understand dependency/core-periphery theory",
            "Compare and evaluate different theories"
          ],
          keyTerms: [
            { term: "Modernization Theory", definition: "Theory that all countries follow similar stages of development, from traditional to modern" },
            { term: "Rostow's Stages", definition: "Five stages of economic growth: traditional, preconditions, take-off, drive to maturity, high consumption" },
            { term: "Dependency Theory", definition: "Theory that poor countries are exploited by rich countries, keeping them underdeveloped" },
            { term: "Core-Periphery Model", definition: "Model showing rich 'core' countries dominating poor 'periphery' countries" },
            { term: "Sustainable Development", definition: "Development that meets present needs without compromising future generations" }
          ],
          studyNotes: `## Theories of Development

### 1. Modernization Theory
**Key idea:** All countries follow the same path of development, from traditional to modern. Developing countries just need to follow the Western model.

**Rostow's Five Stages:**
1. **Traditional society** - Agriculture-based, limited technology
2. **Preconditions for take-off** - Investment, infrastructure, mindset change
3. **Take-off** - Rapid industrial growth, new industries
4. **Drive to maturity** - Diversified economy, technology spreads
5. **High mass consumption** - Service economy, high living standards

**Criticisms:**
- Assumes one path fits all
- Ignores historical exploitation
- Ignores cultural differences
- Western-centric

### 2. Dependency/Core-Periphery Theory
**Key idea:** Poor countries are poor because they have been exploited by rich countries. The global system keeps them dependent.

**The Model:**
- **Core countries** (wealthy, powerful) - USA, Europe, Japan
- **Semi-periphery** - NICs like China, Brazil
- **Periphery** (poor, dependent) - Most of Africa, Pacific Islands

**How exploitation works:**
- Colonial extraction of resources
- Unfair trade terms
- Debt traps
- Brain drain

**Criticisms:**
- Too pessimistic
- Ignores internal problems
- Some countries have developed (NICs)

### 3. Sustainable Development
**Key idea:** Development must balance economic, social, and environmental needs for present and future generations.

**Three Pillars:**
- Economic sustainability
- Social sustainability  
- Environmental sustainability

**The SDGs (Sustainable Development Goals):**
17 goals adopted by UN in 2015, to be achieved by 2030`,
          practiceQuestions: [
            "What are Rostow's five stages of economic growth?",
            "Explain the core-periphery model",
            "What is dependency theory's main argument?",
            "Give two criticisms of modernization theory",
            "What are the three pillars of sustainable development?"
          ],
          examTips: "Be able to compare and contrast the two main theories and discuss their relevance to Vanuatu."
        }
      ]
    },
    strand2: {
      name: "Globalization and Trade",
      units: [
        {
          id: "dst2-13",
          name: "Globalization",
          objectives: [
            "Define globalization",
            "Analyze positive and negative effects",
            "Evaluate impact on Pacific Island countries"
          ],
          keyTerms: [
            { term: "Globalization", definition: "The increasing interconnection of the world's economies, cultures, and populations" },
            { term: "Multinational Corporation (MNC)", definition: "A company that operates in multiple countries" },
            { term: "Free Trade", definition: "Trade between countries without barriers like tariffs or quotas" },
            { term: "WTO", definition: "World Trade Organization - sets rules for international trade" },
            { term: "Cultural Globalization", definition: "The spread of ideas, values, and cultural products around the world" }
          ],
          studyNotes: `## Globalization

### What is Globalization?
The increasing interconnection of the world through:
- Trade and commerce
- Communication and technology
- Movement of people
- Cultural exchange

### Drivers of Globalization
- Technology (internet, transport)
- Trade liberalization
- Multinational corporations
- International organizations (WTO, IMF)

### Positive Effects
✓ Economic growth opportunities
✓ Access to new markets
✓ Technology transfer
✓ Cultural exchange
✓ Lower prices for consumers
✓ Job creation

### Negative Effects
✗ Widening inequality
✗ Job losses in some sectors
✗ Environmental damage
✗ Loss of local culture
✗ Power of multinationals
✗ Vulnerability to global crises

### Globalization in the Pacific
**Opportunities:**
- Tourism growth
- Remittances from workers abroad
- Access to technology
- RSE scheme (seasonal work in NZ)

**Challenges:**
- Small economies, limited bargaining power
- Import dependency
- Brain drain
- Cultural erosion
- Climate vulnerability`,
          practiceQuestions: [
            "Define globalization",
            "Name three drivers of globalization",
            "Give two positive and two negative effects of globalization",
            "How has globalization affected Vanuatu?",
            "What is the RSE scheme and how does it relate to globalization?"
          ],
          examTips: "Be balanced when discussing globalization - acknowledge both benefits and drawbacks with Pacific examples."
        }
      ]
    },
    strand5: {
      name: "Governance and Human Rights",
      units: [
        {
          id: "dst5-13",
          name: "Political Development and Governance",
          objectives: [
            "Define democracy and good governance",
            "Describe Vanuatu's political system",
            "Analyze governance challenges"
          ],
          keyTerms: [
            { term: "Democracy", definition: "Government by the people, through elected representatives" },
            { term: "Good Governance", definition: "Effective, transparent, accountable, and participatory government" },
            { term: "Corruption", definition: "Dishonest or fraudulent conduct by those in power" },
            { term: "Transparency", definition: "Openness in government decision-making and actions" },
            { term: "Accountability", definition: "Being answerable for decisions and actions" }
          ],
          studyNotes: `## Political Development and Governance

### What is Good Governance?
Key principles:
- **Participation** - Citizens involved in decisions
- **Rule of law** - Fair laws applied equally
- **Transparency** - Open government
- **Accountability** - Leaders answer to people
- **Effectiveness** - Services delivered well
- **Equity** - Fair treatment for all

### Vanuatu's Political System
- Constitutional democracy since 1980
- President (ceremonial head of state)
- Prime Minister (head of government)
- Parliament (52 members)
- Provincial governments

### Governance Challenges
- Political instability (frequent votes of no confidence)
- Corruption
- Limited resources
- Capacity constraints
- Urban-rural disparities

### Human Rights
**Universal Declaration of Human Rights (1948):**
- Right to life, liberty, security
- Freedom from torture, slavery
- Right to fair trial
- Freedom of expression, religion
- Right to education, work, healthcare

### Human Rights Issues
- Gender inequality
- Children's rights
- Access to justice
- Land rights
- Environmental rights`,
          practiceQuestions: [
            "What are the key principles of good governance?",
            "Describe Vanuatu's system of government",
            "Name three governance challenges in Vanuatu",
            "What rights are protected in the Universal Declaration of Human Rights?",
            "Why is transparency important in governance?"
          ],
          examTips: "Be able to discuss governance issues specific to Vanuatu and the Pacific region."
        }
      ]
    }
  }
};

export default {
  year11DevelopmentStudentNotes,
  year12DevelopmentStudentNotes,
  year13DevelopmentStudentNotes
};
